import { useEffect, useState, useCallback, useRef } from "react";
import { ChefHat, Printer, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CallButton, PanelLink } from "@/components/CallButton";
import { directPrint } from "@/lib/qz";
import { orderSlip } from "@/lib/receipts";

const NEXT = { pendente: ["preparo", "Iniciar preparo"], preparo: ["pronto", "Marcar pronto"], pronto: ["entregue", "Entregue"] };
const TONE = { pendente: "border-amber-500/50", preparo: "border-primary/60", pronto: "border-emerald-500/60" };
const LABEL = { pendente: "Pendente", preparo: "Em preparo", pronto: "Pronto" };

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    o.connect(ctx.destination);
    o.frequency.value = 880;
    o.start();
    o.stop(ctx.currentTime + 0.25);
  } catch { /* sem áudio */ }
}

function TicketCard({ t, onNext, onPrint, onCalled }) {
  const mins = Math.floor((Date.now() - new Date(t.created_at)) / 60000);
  return (
    <div className={cn("card-surface border-2 p-4 space-y-3 fade-up", TONE[t.status])} data-testid={`production-ticket-${t.number}`}>
      <div className="flex items-start justify-between gap-2">
        <div><p className="font-display text-xl font-bold">#{t.number}</p><p className="text-xs text-muted-foreground">{t.label}</p></div>
        <div className="text-right"><p className="text-xs font-semibold">{LABEL[t.status]}</p><p className={cn("font-mono text-xs", mins > 20 ? "text-red-500" : "text-muted-foreground")}>{fmtTime(t.created_at)} · {mins} min</p></div>
      </div>
      <ul className="space-y-1 text-sm">{t.items.map((i, k) => <li key={k}><b>{i.qty}x</b> {i.name}{i.notes && <p className="text-xs text-amber-600 dark:text-amber-400">{i.notes}</p>}</li>)}</ul>
      {t.status === "pronto" && <CallButton kind="production" item={t} code={t.call?.code || t.number} onDone={onCalled} testId={`production-call-${t.number}`} />}
      <div className="flex gap-2">
        <Button className="flex-1" size="sm" onClick={() => onNext(t)} data-testid={`production-next-${t.number}`}>{NEXT[t.status][1]}</Button>
        {t.print && <Button size="sm" variant="outline" onClick={() => onPrint(t)} data-testid={`production-print-${t.number}`}><Printer className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function PrintTicket({ t, store }) {
  if (!t) return null;
  return (
    <div className="hidden print:block print-area p-2 font-mono text-xs">
      <p className="text-center font-bold">{store} · {t.sector_name}</p>
      <p className="text-center text-base font-bold">PRODUÇÃO #{t.number}</p>
      <p className="text-center">{t.label} · {fmtTime(t.created_at)}</p>
      <p className="border-t border-dashed my-2" />
      {t.items.map((i, k) => <div key={k}><p className="text-sm font-bold">{i.qty}x {i.name}</p>{i.notes && <p>OBS: {i.notes}</p>}</div>)}
    </div>
  );
}

const AUTO_KEY = "pdv_production_autoprint";

export default function Production() {
  const { session } = useAuth();
  const sectors = session.tenant.settings.production_sectors || [];
  const [sector, setSector] = useState(sectors[0]?.id || "");
  const [list, setList] = useState([]);
  const [sound, setSound] = useState(true);
  const [autoPrint, setAutoPrintState] = useState(() => localStorage.getItem(AUTO_KEY) === "1");
  const setAutoPrint = (v) => { localStorage.setItem(AUTO_KEY, v ? "1" : "0"); setAutoPrintState(v); };
  const [printing, setPrinting] = useState(null);
  const seen = useRef(null);
  const print = async (t) => {
    const slip = { number: t.number, label: t.label, customer: t.call?.name, at: t.created_at, user: t.sector_name };
    const ok = await directPrint(session, `sector_${t.sector_id}`, (pr) => orderSlip(slip, `PRODUCAO ${t.sector_name.toUpperCase()}`, t.items, session.tenant.nome_fantasia, pr));
    if (!ok) { setPrinting(t); setTimeout(() => window.print(), 50); }
  };
  const load = useCallback(async () => {
    const { data } = await api.get("/production", { params: { sector } });
    const fresh = seen.current ? data.filter((t) => !seen.current.has(t.id)) : [];
    seen.current = new Set(data.map((t) => t.id));
    if (fresh.length) {
      if (sound) beep();
      toast.info(`${fresh.length} novo(s) pedido(s) na produção`);
      const toPrint = fresh.find((t) => t.print);
      if (autoPrint && toPrint) print(toPrint);
    }
    setList(data);
  }, [sector, sound, autoPrint]);
  useEffect(() => { seen.current = null; }, [sector]);
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);
  const next = async (t) => {
    try { await api.patch(`/production/${t.id}`, { status: NEXT[t.status][0] }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const current = sectors.find((s) => s.id === sector);
  return (
    <>
      <PageHeader eyebrow="Cozinha e bar" title="Produção" subtitle="Atualiza a cada 10 segundos"
        actions={<>
          <PanelLink slug={session.tenant.menu_slug} />
          <Button variant="outline" onClick={() => setSound((s) => !s)} data-testid="production-sound-btn">{sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</Button>
          {current?.print && <label className="flex items-center gap-2 rounded-lg border px-3 text-sm"><Switch checked={autoPrint} onCheckedChange={setAutoPrint} data-testid="production-autoprint-switch" />Imprimir automaticamente</label>}
        </>} />
      <div className="flex gap-2 overflow-x-auto" data-testid="production-sector-tabs">
        {sectors.map((s) => <Button key={s.id} variant={sector === s.id ? "default" : "outline"} onClick={() => setSector(s.id)} data-testid={`production-sector-${s.id}`}>{s.name}{s.print ? " · imprime" : ""}</Button>)}
      </div>
      {!sectors.length ? <EmptyState icon={ChefHat} title="Nenhum setor de produção" text="Cadastre setores em Configurações → Impressão." /> : !list.length ? <EmptyState icon={ChefHat} title="Nada para produzir" text="Os itens com setor definido aparecem aqui quando forem lançados." /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="production-list">{list.map((t) => <TicketCard key={t.id} t={t} onNext={next} onPrint={print} onCalled={load} />)}</div>
      )}
      <PrintTicket t={printing} store={session.tenant.nome_fantasia} />
    </>
  );
}
